import { Component, OnDestroy, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { RoutingService } from '../../service/common/routing/routing.service';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, map, shareReplay, skip, switchMap, takeWhile } from 'rxjs/operators';
import { NetzzuordnungService, Page } from '../../service/netzzuordnung/netzzuordnung.service';
import { combineLatest, distinctUntilChanged, merge, Observable, ReplaySubject, Subject } from 'rxjs';
import { Netzzuordnung } from '../../domain/model/netzzuordnung';
import { TnbUser } from '../../service/auth/tnb-user';
import { NetzzuordnungFilter } from '../../service/netzzuordnung/netzzuordnung-filter';
import { Pageable } from '../../common/paging/pageable';
import { TeilgebietSelectionService } from '../../service/common/teilgebiet/teilgebiet-selection.service';
import { DatePipe } from '@angular/common';
import moment from 'moment';
import { NetzbetreiberService } from '../../service/netzbetreiber/netzbetreiber.service';
import { NotificationService } from 'src/app/service/common/notification/notification.service';
import { distinctUntilFilterChanged } from '../../common/util/helper';

@Component({
    selector: 'tnb-netzzuordnungen-list',
    templateUrl: './netzzuordnungen-list.component.html',
    styleUrls: [
        './netzzuordnungen-list.component.scss',
    ],
    standalone: false,
})
export class NetzzuordnungenListComponent implements OnDestroy, OnInit {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private routeService = inject(RoutingService);
    private netzzuordnungService = inject(NetzzuordnungService);
    private teilgebietSelectionService = inject(TeilgebietSelectionService);
    private netzbetreiberService = inject(NetzbetreiberService);
    private notificationService = inject(NotificationService);
    private changeDetectorRef = inject(ChangeDetectorRef);

    public readonly PAGE_SIZE = 50;

    public page$: Observable<Pageable>;
    public filter$: Observable<NetzzuordnungFilter>;
    public netzzuordnungenPage$: Observable<Page<Netzzuordnung>>;

    public readonly profile: TnbUser;

    private componentDestroyed = false;

    private pageFromUserInteraction$: Observable<Pageable>;
    private pageEventFromUserInteraction$ = new ReplaySubject<PageEvent>(1);
    private sortFromUserInteraction$ = new ReplaySubject<Sort>(1);
    private filterFromUserInteraction$ = new Subject<NetzzuordnungFilter>();

    lastUuidConfirmedDate: Date;
    numberOfUnconfirmedNetzzuordnungen: number;


    constructor() {
        this.profile = this.route.snapshot.data.profile;

        this.setupFilterObservable();
        this.setupPageObservable();
        this.setupNetzzuordnungObservable();
    }

    ngOnInit(): void {
        this.netzzuordnungService.getSnapshot(this.teilgebietSelectionService.getSelectedStadtwerkeId()).subscribe(nzs => {
            this.lastUuidConfirmedDate = new Date(nzs.lastConfirmedUuidDate);
            this.numberOfUnconfirmedNetzzuordnungen = nzs.numberOfUnconfirmedNetzzuordnungen;
            this.changeDetectorRef.markForCheck();
        });
    }

    ngOnDestroy(): void {
        this.componentDestroyed = true;
    }

    private setupPageObservable() {
        this.pageFromUserInteraction$ = combineLatest([
            this.sortFromUserInteraction$,
            this.pageEventFromUserInteraction$,
        ])
            .pipe(
                map(params => {
                    const pageable = new Pageable();

                    const sort = params[0];
                    const page = params[1];

                    pageable.sort = sort?.active || '';
                    pageable.direction = sort?.direction || '';
                    pageable.page = page?.pageIndex || 0;

                    return pageable;
                }),
                shareReplay(1),
            );

        this.route.queryParams
            .pipe(
                takeWhile(() => !this.componentDestroyed),
            )
            .subscribe(params => {
                this.sortFromUserInteraction$.next({
                    active: params['sort'],
                    direction: params['direction'],
                });
                this.pageEventFromUserInteraction$.next({
                    pageIndex: +params['page'],
                } as PageEvent);
            });

        this.page$ = this.pageFromUserInteraction$
            .pipe(
                distinctUntilFilterChanged(),
                shareReplay(1),
            );
    }

    private setupFilterObservable() {
        const filterFromQueryParam$ = this.route.queryParams
            .pipe(
                map((params: any) => {
                    const filter = new NetzzuordnungFilter();
                    if (params) {
                        filter.stadtwerkeId = params['stadtwerkeId'];
                        filter.netzbetreiberName = params['netzbetreiberName'];
                        filter.netzbetreiberTeilgebiet = params['netzbetreiberTeilgebiet'];
                        filter.ortsname = params['ortsname'];
                        filter.stationsname = params['stationsname'];
                        filter.trafoFeldName = params['trafoFeldName'];
                        filter.uuid = params['uuid'];
                        filter.uuidDateStart = this.parseToDate(params['uuidDateStart']);
                        filter.uuidDateEnd = this.parseToDate(params['uuidDateEnd']);
                        filter.technischerPlatz = params['technischerPlatz'];
                        filter.netzgruppe = params['netzgruppe'];
                    }

                    if (this.profile.netzbetreiberType !== 'WESTNETZ') {
                        filter.stadtwerkeId = this.teilgebietSelectionService.getSelectedStadtwerkeId();
                    }
                    return filter;
                }),
            );

        this.filter$ = merge(
            this.filterFromUserInteraction$,
            filterFromQueryParam$,
        ).pipe(
            distinctUntilFilterChanged(),
            shareReplay(1),
        );
    }

    private parseToDate(possibleDate: string | undefined): moment.Moment | undefined {
        const date = moment(possibleDate || null); // undefined means now
        return date.isValid() ? date : undefined;
    }

    private setupNetzzuordnungObservable() {
        // Goto first page, when filter changes
        this.filterFromUserInteraction$
            .pipe(
                skip(1),
                takeWhile(() => !this.componentDestroyed),
            )
            .subscribe(_filter => {
                const page = new PageEvent();
                page.pageIndex = 0;
                this.pageEventFromUserInteraction$.next(page);
            });

        combineLatest([
            this.pageFromUserInteraction$,
            this.filterFromUserInteraction$,
        ])
            .pipe(
                map(param => {
                    const pageable = param[0];
                    const filter = param[1];
                    return this.routeService.netzzuordnungTable(pageable, filter);
                }),
                distinctUntilChanged(),
                takeWhile(() => !this.componentDestroyed),
            )
            .subscribe(url => {
                this.router.navigateByUrl(url);
            });

        this.netzzuordnungenPage$ = combineLatest([
            this.page$,
            this.filter$,
        ])
            .pipe(
                debounceTime(0),
                takeWhile(() => !this.componentDestroyed),
                switchMap(param => this.netzzuordnungService.getAllNetzzuordnungen(param[0], param[1])),
            );
    }

    filterChanged(filter: NetzzuordnungFilter) {
        this.filterFromUserInteraction$.next(filter);
    }

    pageChange($event: PageEvent) {
        this.pageEventFromUserInteraction$.next($event);
    }

    sortChange($event: Sort) {
        this.sortFromUserInteraction$.next($event);
    }

    public updateLastUuidChangeConfirmDate() {
        this.netzbetreiberService.updateLastUuidChangeConfirmDate(this.teilgebietSelectionService.getSelectedStadtwerkeId(), new Date())
            .subscribe(nzs => {
                this.notificationService.success('Uuid Kenntnisnahme bestätigt');
                this.lastUuidConfirmedDate = new Date(nzs.lastConfirmedUuidDate);
                this.numberOfUnconfirmedNetzzuordnungen = nzs.numberOfUnconfirmedNetzzuordnungen;
                this.changeDetectorRef.markForCheck();
            });
    }

    isNumberOfUncofirmedUuidsSmallerorEqual0(): boolean {
        return this.numberOfUnconfirmedNetzzuordnungen <= 0;
    }

    get unconfirmedUuidTooltipText(): string {
        return 'Zuletzt bestätigt am: ' + new DatePipe('de-de').transform(this.lastUuidConfirmedDate, 'dd.MM.yyyy');
    }
}
